import { Routes } from '@angular/router';
import {
  authGuard,
  guestGuard,
  recoveryAuthorizationGuard,
  recoveryChallengeGuard,
  recoverySuccessGuard,
} from './core/guards';
import { BRAND_NAME, brandTitle } from './core/brand';

const page = (
  slug: string,
  title: string,
  fallback = '',
  seoTitle = brandTitle(title),
  seoDescription = '',
) => ({ slug, title, fallback, seoTitle, seoDescription });
export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./features/home/home.component').then((m) => m.HomeComponent),
    title: brandTitle(),
  },
  {
    path: 'shop',
    loadComponent: () => import('./features/catalog/shop.component').then((m) => m.ShopComponent),
    title: brandTitle('Shop Plants'),
  },
  {
    path: 'category/:slug',
    loadComponent: () =>
      import('./features/catalog/listing.component').then((m) => m.ListingComponent),
    title: brandTitle('Shop by Category'),
  },
  {
    path: 'products',
    loadComponent: () =>
      import('./features/catalog/listing.component').then((m) => m.ListingComponent),
    title: brandTitle('All Plants'),
  },
  {
    path: 'search',
    loadComponent: () =>
      import('./features/catalog/listing.component').then((m) => m.ListingComponent),
    title: brandTitle('Search'),
  },
  {
    path: 'product/:slug',
    loadComponent: () =>
      import('./features/product/product-detail.component').then((m) => m.ProductDetailComponent),
  },
  {
    path: 'cart',
    loadComponent: () => import('./features/cart/cart.component').then((m) => m.CartComponent),
    title: brandTitle('Your Cart'),
  },

  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/auth.component').then((m) => m.AuthComponent),
    data: { mode: 'login' },
    title: brandTitle('Log in'),
  },
  {
    path: 'register',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/auth.component').then((m) => m.AuthComponent),
    data: { mode: 'signup' },
    title: brandTitle('Create Account'),
  },
  { path: 'signup', redirectTo: 'register', pathMatch: 'full' },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () => import('./features/auth/password-recovery.component').then((m) => m.ForgotPasswordComponent),
    title: brandTitle('Forgot Password'),
  },
  {
    path: 'forgot-password/verify',
    canActivate: [guestGuard, recoveryChallengeGuard],
    loadComponent: () => import('./features/auth/password-recovery.component').then((m) => m.VerifyRecoveryOtpComponent),
    title: brandTitle('Verify Your Email'),
  },
  {
    path: 'forgot-password/reset',
    canActivate: [guestGuard, recoveryAuthorizationGuard],
    loadComponent: () => import('./features/auth/password-recovery.component').then((m) => m.ResetRecoveredPasswordComponent),
    title: brandTitle('Create New Password'),
  },
  {
    path: 'forgot-password/success',
    canActivate: [guestGuard, recoverySuccessGuard],
    loadComponent: () => import('./features/auth/password-recovery.component').then((m) => m.PasswordRecoverySuccessComponent),
    title: brandTitle('Password Updated'),
  },
  { path: 'verify-otp', redirectTo: '/forgot-password/verify' },
  { path: 'reset-password', redirectTo: '/forgot-password/reset' },
  {
    path: 'complete-mobile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/complete-mobile.component').then((m) => m.CompleteMobileComponent),
    title: brandTitle('Complete Account'),
  },

  {
    path: 'checkout',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/checkout/checkout.component').then((m) => m.CheckoutComponent),
    title: brandTitle('Checkout'),
  },
  {
    path: 'checkout/payment',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/checkout/payment.component').then((m) => m.PaymentComponent),
    title: brandTitle('Payment'),
  },
  {
    path: 'order/success/:orderNo',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/checkout/order-success.component').then((m) => m.OrderSuccessComponent),
    title: brandTitle('Order Confirmed'),
  },

  {
    path: 'account',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'dashboard' },
    title: brandTitle('My Account'),
  },
  {
    path: 'account/profile',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'profile' },
    title: brandTitle('Profile'),
  },
  {
    path: 'account/security',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'security' },
    title: brandTitle('Password & Security'),
  },
  {
    path: 'account/orders',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'orders' },
    title: brandTitle('My Orders'),
  },
  {
    path: 'account/orders/:orderNo',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/order-detail.component').then((m) => m.OrderDetailComponent),
    title: brandTitle('Order Details'),
  },
  {
    path: 'account/returns',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/returns.component').then((m) => m.ReturnsComponent),
    title: brandTitle('My Returns'),
  },
  {
    path: 'account/returns/:requestNumber',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/returns.component').then((m) => m.ReturnsComponent),
    title: brandTitle('Return Status'),
  },
  {
    path: 'account/addresses',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'addresses' },
    title: brandTitle('My Addresses'),
  },
  {
    path: 'account/reviews',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/reviews/my-reviews.component').then((m) => m.MyReviewsComponent),
    title: brandTitle('My Reviews'),
  },
  {
    path: 'account/wishlist',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'wishlist' },
    title: brandTitle('Wishlist'),
  },
  {
    path: 'account/recently-viewed',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/account/account.component').then((m) => m.AccountComponent),
    data: { mode: 'recent' },
    title: brandTitle('Recently Viewed'),
  },

  {
    path: 'about',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    title: 'Grih Upvan | About Us',
    data: page(
      'about',
      `About ${BRAND_NAME}`,
      '<p>Grih Upvan helps customers choose plants, planters and practical gardening essentials with clear care guidance.</p>',
      'Grih Upvan | About Us',
      'Learn how Grih Upvan helps customers choose plants, planters and practical gardening essentials with clear care guidance and dependable support.',
    ),
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    data: page('contact', 'Contact Us', '<p>Our plant care team is ready to help.</p>'),
  },
  {
    path: 'help',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    title: 'Grih Upvan Help & Support',
    data: page(
      'help',
      'Help & Support',
      '<p>Find support for orders, delivery, Cash on Delivery, cancellations, returns, plant care and your account.</p>',
      'Grih Upvan Help & Support',
      'Get help with Grih Upvan orders, delivery, Cash on Delivery, cancellations, return requests, plant care and your customer account.',
    ),
  },
  {
    path: 'faq',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    title: 'Grih Upvan FAQs | Orders, Delivery, Returns & Plant Care',
    data: page(
      'faq',
      'Frequently Asked Questions',
      '<p>Find answers about ordering, delivery, payments, cancellations, returns, accounts and plant care.</p>',
      'Grih Upvan FAQs | Orders, Delivery, Returns & Plant Care',
      'Find clear answers about Grih Upvan orders, PIN-code delivery, Cash on Delivery, cancellations, plant returns, account help and plant care.',
    ),
  },
  {
    path: 'shipping',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    data: page('shipping-policy', 'Shipping Policy'),
  },
  { path: 'shipping-policy', redirectTo: 'shipping' },
  {
    path: 'returns',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    title: 'Grih Upvan Returns & Refunds',
    data: page(
      'return-refund-policy',
      'Returns & Refunds',
      '<p>Eligible delivered live plants may be submitted for return within 72 hours of the recorded delivery time.</p>',
      'Grih Upvan Returns & Refunds',
      'Understand Grih Upvan’s 72-hour return process for eligible delivered live plants, including evidence, review, inspection and refund status.',
    ),
  },
  { path: 'return-refund-policy', redirectTo: 'returns' },
  {
    path: 'plant-care-guide',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    title: 'Plant Care Guide | Grih Upvan',
    data: page(
      'plant-care-guide',
      'Plant Care Guide',
      '<p>Simple habits for happier plants, from choosing light to adjusting water through the seasons.</p>',
      'Plant Care Guide | Grih Upvan',
      'Learn beginner-friendly plant care for Indian homes, including light, watering, drainage, repotting, humidity, feeding and seasonal adjustments.',
    ),
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    data: page('privacy-policy', 'Privacy Policy'),
  },
  { path: 'privacy-policy', redirectTo: 'privacy' },
  {
    path: 'terms',
    loadComponent: () =>
      import('./features/content/content-page.component').then((m) => m.ContentPageComponent),
    data: page('terms', 'Terms & Conditions'),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/content/not-found.component').then((m) => m.NotFoundComponent),
    title: brandTitle('Page Not Found'),
  },
];
